import { Component, DoCheck, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TsInputComponent } from 'tsui';

import { Store } from '@ngxs/store';
import { Account } from '@core/models';
import { AccountState } from '@state/account/state';
import { FormFieldComponent } from '../form-field.types';

const DEFAULT_PRECISION = 0;

@Component({
  templateUrl: './money-form-field.component.html',
  styleUrls: ['./../form-field.component.scss'],
})
export class MoneyFormFieldComponent extends FormFieldComponent implements OnInit, DoCheck {
  @ViewChild(TsInputComponent) private readonly tsInput?: TsInputComponent;

  private account: Account;
  private _originalValue: any;

  public value: any;
  public editing: boolean = false;
  public precision: number = DEFAULT_PRECISION;
  public currencyCode: string | undefined;
  public readonly control = new FormControl<number | string | null>(null);
  // Overrides the inherited Material-specific selector: ts-input has no .mat-form-field-flex to target.
  public readonly commonTargetStylingClass = '.ts-input';

  private readonly store = inject(Store);

  // Duck-typed contract read by form-field.component.ts to avoid clobbering an in-progress edit.
  get inputControl(): ElementRef | undefined {
    return this.tsInput?.inputElement();
  }

  constructor() {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.value = this.model[this.fieldDef.name];

    this._originalValue = this.value;

    if (this.fieldDef.meta && this.fieldDef.meta.scale !== null && this.fieldDef.meta.scale !== undefined) {
      this.precision = this.fieldDef.meta.scale;
    }

    this.currencyCode = this.account?.display_currency;

    this.control.setValue(this.value, { emitEvent: false });
    this.updateDisabledState();

    this.control.valueChanges.pipe(takeUntilDestroyed(this.dRef)).subscribe((value) => {
      this.value = value;
    });
  }

  ngDoCheck(): void {
    this.updateDisabledState();
  }

  updateChangedModel(model: any): void {
    this.model = model;
    this.value = this.model[this.fieldDef.name];
    this._originalValue = this.value;

    this.control.setValue(this.value, { emitEvent: false });
  }

  edit(): void {
    if (!this.editing && !this.isReadonly) {
      this.editing = true;
      setTimeout(() => {
        this.inputControl?.nativeElement.focus();
      }, 300);
    }
  }

  onBlur(): void {
    if (this.value === '') {
      this.value = null;
    }
    this.onSave();
    this.editing = false;
  }

  private onSave(): void {
    if (this._originalValue != this.value) {
      this.updateModel();
      this.update.emit({ [this.fieldDef.name]: this.value });
    }
  }

  private updateModel(): void {
    this.model[this.fieldDef.name] = this.value;
    this._originalValue = this.value;
  }

  private updateDisabledState(): void {
    if (this.isDisabled === this.control.disabled) {
      return;
    }

    if (this.isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }
}
